import { useMemo } from 'react';
import { useUrlState } from '../hooks/use-url-state.js';
import { useDateRange } from '../hooks/use-date-range.js';
import { useServers } from '../hooks/use-servers.js';
import { useConsumption } from '../hooks/use-consumption.js';
import { useServerCosts, useAggregatedCosts } from '../hooks/use-costs.js';
import { DateRangePicker } from '../components/date-range-picker.js';
import { TokenUsageChart } from '../components/token-usage-chart.js';
import { CostChart } from '../components/cost-chart.js';
import { ServiceBreakdownChart } from '../components/service-breakdown-chart.js';
import { SummaryCard } from '../components/summary-card.js';
import { ChartSkeleton } from '../components/chart-skeleton.js';
import { ChartExport } from '../components/chart-export.js';
import { Select } from '../components/select.js';

function formatTokenCount(value: number): string {
  if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}M`;
  if (value >= 1_000) return `${(value / 1_000).toFixed(1)}K`;
  return String(value);
}

function formatUsd(value: number): string {
  return `$${value.toFixed(2)}`;
}

function formatHours(hours: number): string {
  if (hours >= 24) return `${(hours / 24).toFixed(1)}d`;
  return `${hours.toFixed(1)}h`;
}

export function Costs() {
  const { since, until, preset, setRange, setPreset } = useDateRange();
  const { data: servers } = useServers();
  // In the URL, like the period: kept on reload and in shared links
  const [selectedServer, setSelectedServer] = useUrlState<string>('server', '');

  const firstServer = selectedServer || servers?.[0]?.id || '';

  const consumption = useConsumption(firstServer, since, until);
  const serverCosts = useServerCosts(firstServer, since, until);
  const aggregatedCosts = useAggregatedCosts(since, until);

  const tokenCsvRows = useMemo(() =>
    consumption.data?.tokens.datapoints.map((dp) => [
      new Date(dp.timestamp).toLocaleDateString(),
      dp.inputTokens,
      dp.outputTokens,
    ]) ?? [],
  [consumption.data]);

  const costCsvRows = useMemo(() => {
    if (!aggregatedCosts.data) return [];
    const rows: (string | number)[][] = aggregatedCosts.data.servers.map((s) => [
      s.organization || s.serverId,
      s.serverId,
      s.total,
    ]);
    if (aggregatedCosts.data.shared.total > 0) {
      rows.push(['Shared infra', '-', aggregatedCosts.data.shared.total]);
    }
    return rows;
  }, [aggregatedCosts.data]);

  const serviceCsvRows = useMemo(() =>
    serverCosts.data?.breakdown.services
      .filter((s) => s.cost > 0)
      .sort((a, b) => b.cost - a.cost)
      .map((s) => [s.service, s.cost]) ?? [],
  [serverCosts.data]);

  function handleCostBarClick(serverId: string) {
    setSelectedServer(serverId);
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold" style={{ color: 'var(--color-text)' }}>Costs</h1>
        <p className="mt-1 text-sm" style={{ color: 'var(--color-text-2)' }}>Token usage and AWS cost breakdown.</p>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-4">
        <DateRangePicker
          since={since}
          until={until}
          preset={preset}
          onPreset={setPreset}
          onCustomRange={setRange}
        />
        {servers && servers.length > 0 && (
          <Select
            value={selectedServer}
            onChange={(e) => setSelectedServer(e.target.value)}
            aria-label="Select server"
            style={{ color: 'var(--color-text)' }}
          >
            {servers.map((s) => (
              <option key={s.id} value={s.id}>
                {s.organization}
              </option>
            ))}
          </Select>
        )}
      </div>

      {/* Token Usage Section */}
      <section>
        <h2 className="mb-4 text-lg font-semibold" style={{ color: 'var(--color-text)' }}>Token Usage</h2>
        {consumption.isLoading ? (
          <ChartSkeleton />
        ) : consumption.isError ? (
          <div className="rounded-xl border p-4 text-sm" style={{ borderColor: 'var(--color-error)', backgroundColor: 'var(--color-error-dim)', color: 'var(--color-error)' }}>
            Failed to load token data: {consumption.error.message}
          </div>
        ) : consumption.data ? (
          <>
            <div className="mb-4 grid grid-cols-2 gap-4 sm:grid-cols-4">
              <SummaryCard
                label="Input tokens"
                value={formatTokenCount(consumption.data.tokens.total.inputTokens)}
              />
              <SummaryCard
                label="Output tokens"
                value={formatTokenCount(consumption.data.tokens.total.outputTokens)}
              />
              <SummaryCard
                label="Compute"
                value={formatHours(consumption.data.compute.total.runningHours)}
              />
              <SummaryCard
                label="Sessions"
                value={String(consumption.data.sessions.total.count)}
                detail={`avg ${Math.round(consumption.data.sessions.total.avgDurationSeconds / 60)}min`}
              />
            </div>
            <div className="card p-4">
              <div className="mb-2 flex items-center justify-between">
                <h3 className="text-sm font-medium" style={{ color: 'var(--color-text-2)' }}>Daily token usage</h3>
                <ChartExport
                  filename={`token-usage-${firstServer}`}
                  headers={['Date', 'Input Tokens', 'Output Tokens']}
                  rows={tokenCsvRows}
                />
              </div>
              <TokenUsageChart datapoints={consumption.data.tokens.datapoints} />
            </div>
          </>
        ) : null}
      </section>

      {/* AWS Costs Section */}
      <section>
        <h2 className="mb-4 text-lg font-semibold" style={{ color: 'var(--color-text)' }}>AWS Costs</h2>
        {aggregatedCosts.isLoading || serverCosts.isLoading ? (
          <ChartSkeleton />
        ) : aggregatedCosts.isError ? (
          <div className="rounded-xl border p-4 text-sm" style={{ borderColor: 'var(--color-error)', backgroundColor: 'var(--color-error-dim)', color: 'var(--color-error)' }}>
            Failed to load cost data: {aggregatedCosts.error.message}
          </div>
        ) : aggregatedCosts.data ? (
          <>
            <div className="mb-4 grid grid-cols-2 gap-4 sm:grid-cols-3">
              <SummaryCard
                label="Total spend"
                value={formatUsd(aggregatedCosts.data.grandTotal)}
                detail={`Period: ${new Date(aggregatedCosts.data.period.since).toLocaleDateString()} \u2013 ${new Date(aggregatedCosts.data.period.until).toLocaleDateString()}`}
              />
              <SummaryCard
                label="Shared infra"
                value={formatUsd(aggregatedCosts.data.shared.total)}
                detail={`App Runner: ${formatUsd(aggregatedCosts.data.shared.appRunner)}, API GW: ${formatUsd(aggregatedCosts.data.shared.apiGateway)}`}
              />
              {serverCosts.data && (
                <SummaryCard
                  label={`${firstServer} total`}
                  value={formatUsd(serverCosts.data.breakdown.total)}
                  detail={`Data as of ${new Date(serverCosts.data.dataAsOf).toLocaleDateString()}`}
                />
              )}
            </div>

            {/* Aggregated cost chart (all servers) */}
            <div className="card mb-4 p-4">
              <div className="mb-2 flex items-center justify-between">
                <h3 className="text-sm font-medium" style={{ color: 'var(--color-text-2)' }}>Cost by server</h3>
                <ChartExport
                  filename="cost-by-server"
                  headers={['Organization', 'Server ID', 'Cost (USD)']}
                  rows={costCsvRows}
                />
              </div>
              <CostChart
                servers={aggregatedCosts.data.servers}
                shared={aggregatedCosts.data.shared}
                onBarClick={handleCostBarClick}
              />
            </div>

            {/* Per-server service breakdown */}
            {serverCosts.data && serverCosts.data.breakdown.services.length > 0 && (
              <div className="card p-4">
                <div className="mb-2 flex items-center justify-between">
                  <h3 className="text-sm font-medium" style={{ color: 'var(--color-text-2)' }}>
                    {firstServer} — service breakdown
                  </h3>
                  <ChartExport
                    filename={`service-breakdown-${firstServer}`}
                    headers={['Service', 'Cost (USD)']}
                    rows={serviceCsvRows}
                  />
                </div>
                <ServiceBreakdownChart services={serverCosts.data.breakdown.services} />
              </div>
            )}
          </>
        ) : null}
      </section>
    </div>
  );
}
